// Updates tab (per-site): pending plugin/theme/core rows, each selectable, and
// an "Update selected" action that submits ONE job per kind, sequentially.
// Fleet-wide orchestration lives in the Updates hub; this is the drill-down.

import { useState } from 'react';
import { useSiteStatus } from '../hooks';
import { useSubmitUpdates, useJob, useAwaitJob } from '../../updates/hooks';
import { JobProgress } from '../../updates/JobProgress';
import { QueryState } from '../../../components/QueryState';
import { derivePending, buildRequests, type PendingRow } from './updatesSelection';
import type { SiteStatus } from '../../../lib/types';

const ID = 'em-mgr--site--updates';

export interface UpdatesTabProps {
  siteId: string;
  /** In-flight job id from the URL (?job=). Null = none. */
  jobId?: string | null;
  /** Navigate to a job id. Omitted → local fallback state (unit tests). */
  onJobChange?: (jobId: string | null) => void;
}

export function UpdatesTab({ siteId, jobId: routeJobId = null, onJobChange }: UpdatesTabProps) {
  const { data, isPending, isError, error } = useSiteStatus(siteId);
  const submit = useSubmitUpdates();
  const awaitJob = useAwaitJob();

  // Job id lives in the URL: local state alone lost the running job on refresh/navigate-away.
  const [localJobId, setLocalJobId] = useState<string | null>(routeJobId);
  const jobId = onJobChange ? routeJobId : localJobId;
  const setJobId = onJobChange ?? setLocalJobId;
  // Terminal job re-derives this site's pending list so an applied update stops being offered.
  const job = useJob(jobId, siteId);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [queued, setQueued] = useState(0);

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const onSubmit = async (rows: PendingRow[]) => {
    const requests = buildRequests(siteId, rows, selected);
    if (requests.length === 0) return;
    setQueued(requests.length);
    // SEQUENTIAL, each awaited to terminal before the next starts — concurrent
    // jobs would race two WordPress upgraders on the same site.
    for (const req of requests) {
      const res = await submit.mutateAsync(req.body);
      setJobId(res.jobId);
      await awaitJob(res.jobId);
    }
    setQueued(0);
    setSelected(new Set());
  };


  return (
    <QueryState<SiteStatus>
      idBase={ID}
      isPending={isPending}
      isError={isError}
      error={error}
      data={data}
    >
      {(s) => {
        const pending = derivePending(s);
        if (pending.length === 0) {
          return (
            <div
              data-id={`${ID}--uptodate`}
              className="rounded-lg border border-dashed border-green-300 p-6 text-center text-green-700 dark:border-green-800 dark:text-green-400"
            >
              Everything is up to date.
            </div>
          );
        }
        return (
          <div data-id={ID} className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="mr-auto text-sm text-slate-500">
                {selected.size} of {pending.length} selected
              </span>
              <button
                type="button"
                data-id={`${ID}--submit`}
                disabled={selected.size === 0 || submit.isPending || queued > 0}
                onClick={() => onSubmit(pending)}
                className="rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
              >
                {queued > 0 ? `Updating… (${queued} left)` : 'Update selected'}
              </button>
            </div>

            {job.data && <JobProgress job={job.data} idBase={ID} />}

            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900">
                  <tr>
                    <th className="w-8 px-3 py-2" />
                    <th className="px-3 py-2">Item</th>
                    <th className="px-3 py-2">Kind</th>
                    <th className="px-3 py-2">From</th>
                    <th className="px-3 py-2">To</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((r) => (
                    <tr
                      key={r.key}
                      data-id={`${ID}--row`}
                      className="border-t border-slate-100 dark:border-slate-800"
                    >
                      <td className="px-3 py-2">
                        {/* Core stays selectable even when disabled (em-manager 403s) so the 403 surfaces in the job result instead of hiding silently. */}
                        <input
                          type="checkbox"
                          data-id={`${ID}--select`}
                          checked={selected.has(r.key)}
                          onChange={() => toggle(r.key)}
                          aria-label={`Select ${r.name}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium">
                        {r.name}
                        {r.isEm && (
                          <span className="ml-1 rounded bg-slate-100 px-1 text-xs dark:bg-slate-800">
                            em
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{r.kind}</td>
                      <td className="px-3 py-2">{r.from}</td>
                      <td className="px-3 py-2 text-blue-600 dark:text-blue-400">{r.to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }}
    </QueryState>
  );
}
